import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Repositories } from '@core/db';
import { OPEN_CASE_STATUSES, type Case, type CaseEvent, type Client, type EventType } from '@core/domain/model';
import { dayMonthWeekday, monthYear } from '@core/ui/RuDates';
import { telemetry } from '@core/telemetry';
import {
  daysOf,
  eventsOfDay,
  shiftMonth,
  startOfDay,
  startOfMonth,
  type CalendarDay,
  type CalendarEventRow,
} from './month';

export interface CalendarState {
  isLoading: boolean;
  /** Полночь первого числа показанного месяца. */
  month: number;
  /** «Сентябрь 2026» */
  monthLabel: string;
  days: CalendarDay[];
  selectedDate: number;
  /** «14 сентября, понедельник» */
  selectedLabel: string;
  events: CalendarEventRow[];
  /** Дела для выбора при заведении события. */
  cases: { id: string; title: string }[];
}

/**
 * Данные календаря: месяц и день на одном экране.
 *
 * Два представления одной сущности вместе с «Задачами» ([DATA_MODEL.md]): событие
 * с типом «Задача» видно и там, и здесь — это не дубль, а один и тот же срок.
 */
export function useCalendar(db: Repositories, now: () => number = Date.now) {
  const [month, setMonth] = useState(() => startOfMonth(now()));
  const [selected, setSelected] = useState(() => startOfDay(now()));

  const [events, setEvents] = useState<CaseEvent[]>([]);
  const [cases, setCases] = useState<Case[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setLoading] = useState(true);
  const [version, setVersion] = useState(0);

  const refresh = useCallback(() => setVersion((value) => value + 1), []);

  useEffect(() => {
    let dropped = false;

    void (async () => {
      const [allEvents, allCases, allClients] = await Promise.all([
        db.events.all(),
        db.cases.list({}),
        db.clients.list(),
      ]);
      if (dropped) return;

      setEvents(allEvents);
      setCases(allCases);
      setClients(allClients);
      setLoading(false);
    })();

    return () => {
      dropped = true;
    };
  }, [db, version]);

  const state: CalendarState = useMemo(() => {
    const titles = new Map(cases.map((item) => [item.id, item.title]));

    return {
      isLoading,
      month,
      monthLabel: monthYear(new Date(month)),
      days: daysOf(month, selected, now(), events, clients),
      selectedDate: selected,
      selectedLabel: dayMonthWeekday(new Date(selected)),
      events: eventsOfDay(selected, events, clients, titles),
      cases: cases
        .filter((item) => OPEN_CASE_STATUSES.includes(item.status))
        .map((item) => ({ id: item.id, title: item.title })),
    };
  }, [month, selected, events, cases, clients, isLoading, now]);

  const actions = useMemo(
    () => ({
      /**
       * Выбор дня из соседнего месяца перелистывает месяц.
       *
       * Иначе выбранная клетка уезжает из вида: юрист нажал 1 октября в хвосте сентября
       * и не понимает, куда делось выделение.
       */
      selectDate(date: number) {
        setSelected(startOfDay(date));
        setMonth(startOfMonth(date));
      },

      previousMonth: () => setMonth((current) => shiftMonth(current, -1)),
      nextMonth: () => setMonth((current) => shiftMonth(current, 1)),

      today() {
        setSelected(startOfDay(now()));
        setMonth(startOfMonth(now()));
      },

      /**
       * Новое событие: тип, название, время и — по желанию — дело.
       *
       * Напоминание сохраняется, но **не приходит**: на iOS оно возможно только пушем
       * с нашего сервера, и это этап 7 ([D-45](docs/DECISIONS.md)). Обещать в интерфейсе
       * то, чего нет, нельзя, поэтому поля напоминания здесь пока нет вовсе.
       */
      async createEvent(draft: {
        title: string;
        type: EventType;
        startAt: number;
        caseId: string | null;
      }) {
        const title = draft.title.trim();
        if (title.length === 0) return;

        await db.events.create({
          id: '',
          title,
          type: draft.type,
          startAt: draft.startAt,
          endAt: null,
          description: null,
          caseId: draft.caseId,
          clientId: null,
          reminderMinutesBefore: null,
          isCompleted: false,
          deadlineAt: draft.type === 'Task' ? draft.startAt : null,
        });
        // Задача, заведённая из календаря, — всё-таки задача: иначе «создано задач»
        // зависело бы от того, с какого экрана её завели.
        telemetry.record(draft.type === 'Task' ? 'taskCreated' : 'eventCreated');
        refresh();
      },

      refresh,
    }),
    [db, refresh, now],
  );

  return { state, actions };
}

export type CalendarActions = ReturnType<typeof useCalendar>['actions'];
